/**
 * Advanced-list MATERIALIZATION control API contracts (plan 2026-06-09-001).
 *
 * Two admin-only write surfaces consumed by the web Lists Explorer (U10):
 *   - U7 POST /v1/schools/:id/lists/:list_id/materialization
 *        enable | disable | designate (single-column targeting contract, KTD4a)
 *   - U6 POST /v1/schools/:id/lists/:list_id/refresh
 *        per-list "refresh now"; coalesces with an in-flight sync (KTD lease).
 *
 * These mirror the backend use-case response shapes
 * (`advanced-lists/{materialization,refresh}/schema.ts`) so the web client
 * imports its types from shared-types, never from a local API type.
 */

import { z } from 'zod'

// ───────────────────────────────────────────────────────────────────────────
// U7 — materialization (enable / disable / designate)
// ───────────────────────────────────────────────────────────────────────────

/**
 * Request body discriminated by `action`. `designate` carries the single
 * authoritative column ordinal + its verbatim BB name (KTD9). snake_case is NOT
 * used here — the backend schema reads camelCase body keys for this endpoint.
 */
export const MaterializationActionRequestSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('enable') }),
  z.object({ action: z.literal('disable') }),
  z.object({
    action: z.literal('designate'),
    designatedColumnOrdinal: z.number().int().min(0),
    designatedColumnName: z.string().min(1)
  })
])

export type MaterializationActionRequest = z.infer<typeof MaterializationActionRequestSchema>

export const MaterializationResponseDataSchema = z.object({
  listId: z.string(),
  enabled: z.boolean(),
  /** Targetable only once a column is confirmed (KTD4a). */
  targetable: z.boolean(),
  designatedColumnName: z.string().nullable(),
  designatedColumnOrdinal: z.number().int().min(0).nullable(),
  activeGeneration: z.number().nullable(),
  bbRowCount: z.number().min(0).nullable(),
  resolvedUserCount: z.number().min(0).nullable(),
  lastSyncStatus: z.string().nullable()
})

export type MaterializationResponseData = z.infer<typeof MaterializationResponseDataSchema>

export const MaterializationResponseSchema = z.object({
  data: MaterializationResponseDataSchema,
  message: z.string()
})

export type MaterializationResponse = z.infer<typeof MaterializationResponseSchema>

// ───────────────────────────────────────────────────────────────────────────
// U6 — refresh now
// ───────────────────────────────────────────────────────────────────────────

/** No request body — the action is fully determined by the path. */
export const RefreshListRequestSchema = z.object({})

export type RefreshListRequest = z.infer<typeof RefreshListRequestSchema>

/** `SYNCED` (writer ran) or `ALREADY_IN_PROGRESS` (coalesced with in-flight). */
export const RefreshListOutcomeSchema = z.enum(['SYNCED', 'ALREADY_IN_PROGRESS'])

export type RefreshListOutcome = z.infer<typeof RefreshListOutcomeSchema>

export const RefreshListResponseDataSchema = z.object({
  listId: z.string(),
  outcome: RefreshListOutcomeSchema,
  /** Writer terminal status when SYNCED (SUCCESS/EMPTY/FAILED_AUTH/…); null otherwise. */
  status: z.string().nullable(),
  /** True only when this refresh flipped to a new snapshot generation. */
  swapped: z.boolean(),
  bbRowCount: z.number().min(0).nullable(),
  resolvedUserCount: z.number().min(0).nullable(),
  targetable: z.boolean().nullable()
})

export type RefreshListResponseData = z.infer<typeof RefreshListResponseDataSchema>

export const RefreshListResponseSchema = z.object({
  data: RefreshListResponseDataSchema,
  message: z.string()
})

export type RefreshListResponse = z.infer<typeof RefreshListResponseSchema>
